export const DEFAULT_STORY_COACH_ANSWERS = {
  threshold: '',
  anchor: '',
  peak: '',
  afterglow: '',
};

export const generateStoryCoachAnswersFromBrief = (brief = '', pageData = {}) => {
  const text = String(brief || '').trim();
  if (!text) return DEFAULT_STORY_COACH_ANSWERS;

  const lower = text.toLowerCase();
  const recipient = pageData.ownerName || 'you';
  const isMom = lower.includes('mom') || lower.includes('mother');
  const isBirthday = lower.includes('birthday') || lower.includes('50') || lower.includes('60');
  const isPartner = lower.includes('anniversary') || lower.includes('partner') || lower.includes('wife') || lower.includes('husband');
  const isMemorial = lower.includes('memorial') || lower.includes('pet') || lower.includes('remember');
  const isBaby = lower.includes('baby') || lower.includes('newborn') || lower.includes('family');

  const occasion = isBirthday ? 'birthday' :
    isPartner ? 'anniversary' :
      isMemorial ? 'memory' :
        isBaby ? 'family milestone' :
          'special moment';

  const relationshipLine = isMom ?
    'I wanted to make something that feels warmer than a card, because you have carried so much love for our family.' :
    isPartner ?
      'I wanted this to feel like opening a personal piece of our story, not just another link.' :
      isMemorial ?
        'I wanted to hold this memory gently, with room for love, grief, and gratitude to sit together.' :
        isBaby ?
          'I wanted to save this tiny season before it changes, so our family can come back to it later.' :
          `I wanted ${recipient} to open something personal, thoughtful, and made for this exact moment.`;

  return {
    threshold: `${relationshipLine} This gift is for ${occasion}: ${text}.`,
    anchor: `Start with the photo or video that instantly says, "this is us." For this brief, choose the image that feels most recognizable to ${recipient}.`,
    peak: isMom ?
      'Say out loud one thing you are grateful for and one thing you hope she feels every time she opens this.' :
      isMemorial ?
        'Record a gentle memory, a thank-you, or the small detail you never want to forget.' :
        `Record the sentence you would want ${recipient} to hear if they only played one part of this gift.`,
    afterglow: isBirthday ?
      `Keep this as proof that ${recipient} is deeply loved in this season of life.` :
      isMemorial ?
        'Come back here whenever remembering needs a softer place to land.' :
        `Come back here whenever you want to remember how much this moment mattered.`,
  };
};

export const STORY_COACH_PROMPTS = [
  {
    key: 'threshold',
    title: 'Reason',
    prompt: 'Why did you make this gift for them right now?',
    placeholder: 'Example: I wanted you to open this before the trip, so you know how much this moment means.',
    suggestedModule: 'text',
    action: 'Add Note',
  },
  {
    key: 'anchor',
    title: 'First Memory',
    prompt: 'Choose one photo or video they will recognize immediately.',
    placeholder: 'Example: Use the photo from the day everything felt easy.',
    suggestedModule: 'primary_media',
    action: 'Add Media',
  },
  {
    key: 'peak',
    title: 'Heart',
    prompt: 'What have you wanted to say but never said out loud?',
    placeholder: 'Example: I am proud of you in a way I do not say often enough.',
    suggestedModule: 'voice_postcard',
    action: 'Add Voice',
  },
  {
    key: 'afterglow',
    title: 'Final Wish',
    prompt: 'End with one thing they can keep, replay, or come back to.',
    placeholder: 'Example: Come back here whenever you need proof this mattered.',
    suggestedModule: 'keepsake_poster',
    action: 'Add Final Card',
  },
];

const STORY_COACH_CREATIVE_RECOMMENDATIONS = {
  partner: [
    {
      moduleType: 'then_now',
      label: 'Then & Now',
      reason: 'Best for anniversaries or couples because it shows how the relationship has changed over time.',
      prompt: 'Add an early photo and a recent photo so the gift feels like a real before-and-after love story.',
    },
  ],
  new_baby: [
    {
      moduleType: 'memory_map',
      label: 'Milestones',
      reason: 'Best for baby and family gifts because tiny firsts are easier to keep when they have dates and short notes.',
      prompt: 'Add the first hello, first family moment, and one tiny first so the gift starts like a baby milestone path.',
    },
    {
      moduleType: 'frame_gallery',
      label: 'Baby Gallery',
      reason: 'Best for baby gifts when one favorite photo should feel like a framed keepsake inside the album.',
      prompt: 'Choose one soft baby photo and frame it as a small keepsake print.',
    },
    {
      moduleType: 'timeline',
      label: 'Growth Timeline',
      reason: 'Best for families who want this NFC gift to keep growing after the first scan.',
      prompt: 'Add newborn days, one month, and the next tiny chapter so the page can become a living growth timeline.',
    },
  ],
  graduation: [
    {
      moduleType: 'then_now',
      label: 'Then & Now',
      reason: 'Best for graduation because it shows the distance between where they started and where they are now.',
      prompt: 'Use one photo from the beginning and one from the achievement moment.',
    },
  ],
  remembrance: [
    {
      moduleType: 'primary_media',
      label: 'Memory Archive',
      reason: 'Best for remembrance gifts because photos and short clips give the archive a clear emotional anchor.',
      prompt: 'Choose the photos or short clips that feel most true to the person being remembered.',
    },
    {
      moduleType: 'voice_postcard',
      label: 'Voice Archive',
      reason: 'Best for remembrance gifts because spoken stories can feel more personal than written notes.',
      prompt: 'Record a story, blessing, or message the family may want to hear again later.',
    },
    {
      moduleType: 'timeline',
      label: 'Timeline',
      reason: 'Best for remembrance gifts because families often need a gentle path through important memories.',
      prompt: 'Add a beginning, a life-giving memory, and what the family carries forward.',
    },
    {
      moduleType: 'guestbook',
      label: 'Family Messages',
      reason: 'Best for remembrance gifts because it lets close loved ones add memories into the same archive.',
      prompt: 'Invite family to leave a short memory, blessing, or note for the archive.',
    },
  ],
  pet_memorial: [
    {
      moduleType: 'frame_gallery',
      label: 'Frame Gallery',
      reason: 'Best for memorial gifts because a single framed image feels quieter and more respectful.',
      prompt: 'Choose the photo that feels most like them, then frame it as a keepsake print.',
    },
  ],
  thank_you: [
    {
      moduleType: 'frame_gallery',
      label: 'Frame Gallery',
      reason: 'Best for appreciation gifts because one framed memory can feel more intentional than a full album.',
      prompt: 'Pick the image or moment that explains why you are grateful.',
    },
  ],
  birthday: [
    {
      moduleType: 'polaroid_booth',
      label: 'Polaroid Booth',
      reason: 'Best for birthdays because it gives the recipient a playful way to send a selfie or quick reaction back.',
      prompt: 'Let them add a birthday reaction as part of the keepsake.',
    },
  ],
  bestie: [
    {
      moduleType: 'polaroid_booth',
      label: 'Polaroid Booth',
      reason: 'Best for best-friend gifts because the reply moment should feel casual, fun, and alive.',
      prompt: 'Let them add a selfie, emoji, or quick video reply after opening the gift.',
    },
  ],
};

const detectStoryCoachContext = (pageData = {}) => {
  const explicitKey = pageData.templateKey || pageData.presetKey || pageData.preset || pageData.template;
  if (STORY_COACH_CREATIVE_RECOMMENDATIONS[explicitKey]) return explicitKey;

  const text = [
    explicitKey,
    pageData.giftFormat,
    pageData.openingTitle,
    pageData.ownerName,
    pageData.storyCoachAnswers?.threshold,
    pageData.storyCoachAnswers?.anchor,
    pageData.storyCoachAnswers?.peak,
    pageData.storyCoachAnswers?.afterglow,
  ].filter(Boolean).join(' ').toLowerCase();

  if (text.includes('anniversary') || text.includes('partner') || text.includes('wife') || text.includes('husband') || text.includes('love')) {
    return 'partner';
  }
  if (text.includes('baby') || text.includes('newborn') || text.includes('family')) {
    return 'new_baby';
  }
  if (text.includes('graduation') || text.includes('graduate')) {
    return 'graduation';
  }
  if (text.includes('pet') || text.includes('dog') || text.includes('cat')) {
    return 'pet_memorial';
  }
  if (text.includes('remembrance') || text.includes('memorial') || text.includes('remember') || text.includes('forever remembered') || text.includes('forever loved')) {
    return 'remembrance';
  }
  if (text.includes('thank') || text.includes('appreciation') || text.includes('grateful')) {
    return 'thank_you';
  }
  if (text.includes('birthday')) {
    return 'birthday';
  }
  if (text.includes('bestie') || text.includes('best friend') || text.includes('friend')) {
    return 'bestie';
  }
  return '';
};

export const getStoryCoachCreativeRecommendations = (pageData = {}) => {
  const context = detectStoryCoachContext(pageData);
  const modules = Array.isArray(pageData.modules) ? pageData.modules : [];
  const existingTypes = new Set(modules.map((module) => module.type));

  return (STORY_COACH_CREATIVE_RECOMMENDATIONS[context] || [])
    .map((recommendation) => ({
      ...recommendation,
      context,
      alreadyAdded: existingTypes.has(recommendation.moduleType),
    }));
};

const getTemplateKey = (pageData = {}) => (
  pageData.templateKey || pageData.presetKey || pageData.preset || pageData.template || ''
);

export const createStoryModule = (type, index, pageData = {}) => {
  const id = `story_${type}_${Date.now()}_${index}`;
  const templateKey = getTemplateKey(pageData);
  const isNewBabyTemplate = templateKey === 'new_baby';
  const isRemembranceTemplate = templateKey === 'remembrance';
  const remembranceTimelineItems = [
    {
      id: `tl_${Date.now()}_1`,
      label: 'A beginning worth remembering',
      dateLabel: '',
      note: 'Add an early memory, place, or family story that helps begin the archive gently.',
      iconType: 'heart',
      linkedModuleId: '',
    },
    {
      id: `tl_${Date.now()}_2`,
      label: 'A life-giving memory',
      dateLabel: '',
      note: 'Save a moment that shows their personality, care, humor, strength, or everyday presence.',
      iconType: 'photo',
      linkedModuleId: '',
    },
    {
      id: `tl_${Date.now()}_3`,
      label: 'What we carry forward',
      dateLabel: '',
      note: 'Add the lesson, tradition, phrase, or love that family keeps carrying from here.',
      iconType: 'quote',
      linkedModuleId: '',
    },
  ];
  const defaults = {
    primary_media: {
      type,
      data: {
        ...(isNewBabyTemplate ? { title: 'Baby Gallery' } : {}),
        ...(isRemembranceTemplate ? { title: 'Memory Archive' } : {}),
        mediaType: 'photo',
        images: [],
        caption: isNewBabyTemplate ?
          'Add the first baby photos and family moments everyone will want to revisit.' :
          isRemembranceTemplate ?
            'Begin with photos or short clips that feel true to their life: a favorite place, a family table, a smile, or a moment everyone still remembers.' :
            'Start with the photo or video that should be seen first.',
        videoCaption: '',
      },
    },
    text: {
      type,
      data: {
        title: 'Before You Scroll',
        body: 'This NFC gift was made to hold a small story: a first photo, a personal note, a hidden surprise, and a final moment to keep.',
      },
    },
    ai_generator: {
      type,
      data: {
        title: 'AI Studio',
        contentType: 'letter',
        prompt: '',
        savedDrafts: [],
      },
    },
    surprise_box: {
      type,
      data: {
        label: 'Open When',
        hint: 'Open this when you want the surprise part.',
        content: 'A small reminder: this was not a random link. It was made, saved, and sealed for you.',
        letters: [
          {
            id: `open_when_${Date.now()}_1`,
            label: 'Open When',
            prompt: 'Open this when you need a soft reminder',
            message: 'A small reminder: this was not a random link. It was made, saved, and sealed for you.',
          },
        ],
      },
    },
    flip_card: {
      type,
      data: {
        title: 'A Little Memory',
        clue: 'Tap to unlock a small thing I wanted you to keep.',
        message: 'One more thing I wanted you to know...',
        signature: 'Saved for you',
        frontContent: 'Tap to Reveal',
        backContent: 'One more thing I wanted you to know...',
      },
    },
    tiny_museum: {
      type,
      data: {
        title: 'A Little Memory',
        clue: 'Tap to unlock a small thing I wanted you to keep.',
        message: 'One more thing I wanted you to know...',
        signature: 'Saved for you',
        frontContent: 'Tap to Reveal',
        backContent: 'One more thing I wanted you to know...',
      },
    },
    voice_postcard: {
      type,
      data: {
        title: isRemembranceTemplate ? 'Voice Archive' : 'A Voice Note',
        prompt: isRemembranceTemplate ?
          'Record a memory, a story, a blessing, or the words the family may want to hear again later.' :
          'Record the part that is better heard than typed.',
        audioUrl: null,
        recordedUrl: null,
      },
    },
    song_dedication: {
      type,
      data: {
        title: 'A Song for This Moment',
        artist: '',
        dedication: 'Add a song that carries the feeling of this gift.',
      },
    },
    timeline: {
      type,
      data: {
        title: isNewBabyTemplate ? 'Growth Timeline' : isRemembranceTemplate ? 'Timeline' : 'Moments to Keep',
        timelineItems: isNewBabyTemplate ? [
          {
            id: `tl_${Date.now()}_1`,
            label: 'Newborn days',
            dateLabel: '',
            note: 'Save the first days: tiny details, first home photos, and the earliest family memories.',
            iconType: 'heart',
            linkedModuleId: '',
          },
          {
            id: `tl_${Date.now()}_2`,
            label: 'One month',
            dateLabel: '',
            note: 'Add a note about what changed, what they loved, and the little personality already appearing.',
            iconType: 'photo',
            linkedModuleId: '',
          },
        ] : isRemembranceTemplate ? remembranceTimelineItems : [
          {
            id: `tl_${Date.now()}_1`,
            label: 'The first moment',
            dateLabel: '',
            note: '',
            iconType: 'heart',
            linkedModuleId: '',
          },
          {
            id: `tl_${Date.now()}_2`,
            label: 'The memory to replay',
            dateLabel: '',
            note: '',
            iconType: 'photo',
            linkedModuleId: '',
          },
        ],
      },
    },
    memory_map: {
      type,
      data: {
        title: isNewBabyTemplate ? 'Milestones' : isRemembranceTemplate ? 'Places We Remember' : 'Moments to Keep',
        ...(isNewBabyTemplate ? {
          subtitle: 'Tiny firsts, family moments, and little dates worth saving.',
          displayKicker: 'Milestone path',
        } : {}),
        ...(isRemembranceTemplate ? {
          subtitle: 'Places, dates, and small anchors that help the family find their way back to the memories.',
          displayKicker: 'Memory path',
        } : {}),
        timelineItems: isNewBabyTemplate ? [
          {
            id: `tl_${Date.now()}_1`,
            label: 'First hello',
            dateLabel: '',
            note: 'Add the first photo, tiny hand, hospital moment, or the day everyone finally met.',
            iconType: 'heart',
            linkedModuleId: '',
          },
          {
            id: `tl_${Date.now()}_2`,
            label: 'First family moment',
            dateLabel: '',
            note: 'Save the first quiet family photo, homecoming, sibling meeting, or favorite early routine.',
            iconType: 'photo',
            linkedModuleId: '',
          },
        ] : isRemembranceTemplate ? remembranceTimelineItems : [
          {
            id: `tl_${Date.now()}_1`,
            label: 'The first memory',
            dateLabel: '',
            note: '',
            iconType: 'heart',
            linkedModuleId: '',
          },
          {
            id: `tl_${Date.now()}_2`,
            label: 'The memory to replay',
            dateLabel: '',
            note: '',
            iconType: 'photo',
            linkedModuleId: '',
          },
        ],
      },
    },
    reaction: {
      type,
      data: {
        label: 'Send a Reply',
        title: 'Send a Reaction',
        subtitle: 'Add a quick selfie, emoji, or video reply',
      },
    },
    polaroid_booth: {
      type,
      data: {
        label: 'Send a Reply',
        title: 'Polaroid Booth',
        subtitle: 'Add a quick selfie, emoji, or video reply',
      },
    },
    then_now: {
      type,
      data: {
        title: 'Then & Now',
        subtitle: 'A small look at how far this story has come.',
        beforeImage: '',
        afterImage: '',
      },
    },
    frame_gallery: {
      type,
      data: {
        title: 'A Framed Memory',
        caption: isRemembranceTemplate ? 'Choose one image that feels gentle, true, and worth keeping close.' : 'A small memory worth framing.',
        imageUrl: '',
        frameStyle: 'paper',
      },
    },
    guestbook: {
      type,
      data: {
        title: isNewBabyTemplate ? 'Family Wishes' : isRemembranceTemplate ? 'Family Messages' : 'Blessing Wall',
        subtitle: isNewBabyTemplate ?
          'Warm notes from family and loved ones for this little beginning.' :
          isRemembranceTemplate ?
            'Gentle notes, memories, and words from family and close loved ones.' :
            'Notes from people invited into this gift.',
        ...(isRemembranceTemplate ? {
          displayLabel: 'Family Messages',
          formTitle: 'Leave a memory',
          formDescription: 'Write a short memory, blessing, or message for the family archive.',
          successTitle: 'Message saved',
          successMessage: 'Your memory has been saved with this remembrance keepsake.',
        } : {}),
      },
    },
    keepsake_poster: {
      type,
      data: {
        title: 'Keep This Moment',
        senderName: isRemembranceTemplate ? 'With Love' : 'Someone Special',
        message: isRemembranceTemplate ?
          'A quiet place for the stories, voices, and messages that still matter.' :
          'A small piece of this NFC gift, kept in one place.',
        closingLine: isRemembranceTemplate ?
          'Return here whenever remembering needs a gentle place to land.' :
          'Come back whenever you need to remember this.',
        posterStyle: 'macaron',
      },
    },
  };

  return { id, ...(defaults[type] || { type, data: {} }) };
};

export const getStoryCoachModuleData = (key, answer, existingData = {}, pageData = {}) => {
  const templateKey = getTemplateKey(pageData);
  const isRemembranceTemplate = templateKey === 'remembrance';

  if (key === 'threshold') {
    return {
      ...existingData,
      title: existingData.title || 'Why I Made This',
      body: answer,
    };
  }
  if (key === 'anchor') {
    const images = Array.isArray(existingData.images) ? existingData.images : [];
    return {
      ...existingData,
      title: existingData.title || (isRemembranceTemplate ? 'Memory Archive' : undefined),
      mediaType: existingData.mediaType || 'photo',
      caption: answer,
      images: images.map((image, index) => index === 0 ? { ...image, caption: image.caption || answer } : image),
    };
  }
  if (key === 'peak') {
    return {
      ...existingData,
      title: existingData.title || (isRemembranceTemplate ? 'Voice Archive' : 'A Voice Note'),
      prompt: answer,
    };
  }
  if (key === 'afterglow') {
    return {
      ...existingData,
      title: existingData.title || 'Keep This Moment',
      senderName: existingData.senderName || (isRemembranceTemplate ? 'With Love' : pageData.ownerName || 'Someone Special'),
      message: answer,
      closingLine: existingData.closingLine || (isRemembranceTemplate ?
        'Return here whenever remembering needs a gentle place to land.' :
        'Come back whenever you need to remember this.'),
      posterStyle: existingData.posterStyle || 'macaron',
    };
  }
  return existingData;
};

const getCoachAnswers = (pageData = {}) => ({
  threshold: (pageData.storyCoachAnswers?.threshold || '').trim(),
  anchor: (pageData.storyCoachAnswers?.anchor || '').trim(),
  peak: (pageData.storyCoachAnswers?.peak || '').trim(),
  afterglow: (pageData.storyCoachAnswers?.afterglow || '').trim(),
});

const makeCoachId = (prefix) => `coach_${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const updateFirstModuleOfType = (modules, typeOrTypes, updater, pageData = {}) => {
  const types = Array.isArray(typeOrTypes) ? typeOrTypes : [typeOrTypes];
  const createType = types[0];
  let updated = false;
  const nextModules = modules.map((module) => {
    if (!types.includes(module.type) || updated) return module;
    updated = true;
    return {
      ...module,
      data: updater(module.data || {}),
    };
  });

  if (updated) return nextModules;

  const createdModule = createStoryModule(
    createType,
    nextModules.filter((module) => types.includes(module.type)).length,
    pageData
  );

  return [
    ...nextModules,
    {
      ...createdModule,
      id: makeCoachId(createType),
      data: updater(createdModule.data || {}),
    },
  ];
};

export const applyStoryCoachAnswersToStory = (pageData = {}) => {
  const answers = getCoachAnswers(pageData);
  if (!answers.threshold && !answers.anchor && !answers.peak && !answers.afterglow) return pageData;

  let modules = Array.isArray(pageData.modules) ? [...pageData.modules] : [];

  if (answers.anchor) {
    modules = updateFirstModuleOfType(modules, 'primary_media', (data) => {
      const images = Array.isArray(data.images) ? data.images : [];
      return {
        ...data,
        mediaType: data.mediaType || 'photo',
        caption: answers.anchor,
        images: images.map((image, index) => index === 0 ? { ...image, caption: image.caption || answers.anchor } : image),
      };
    }, pageData);
  }

  if (answers.threshold) {
    modules = updateFirstModuleOfType(modules, 'text', (data) => ({
      ...data,
      title: data.title && data.title !== 'Before You Scroll' ? data.title : 'Why I Made This',
      body: [
        answers.threshold,
        answers.peak ? `The part I hope you feel most is this: ${answers.peak}` : '',
        answers.afterglow ? `And when you come back later, remember this: ${answers.afterglow}` : '',
      ].filter(Boolean).join('\n\n'),
    }), pageData);
  }

  if (answers.peak || answers.afterglow) {
    const firstMessage = answers.peak || answers.threshold || answers.afterglow;
    const secondMessage = answers.afterglow || answers.peak;
    modules = updateFirstModuleOfType(modules, 'surprise_box', (data) => {
      const existingLetters = Array.isArray(data.letters) ? data.letters : [];
      const firstLetter = existingLetters[0] || {};
      const secondLetter = existingLetters[1] || {};
      const letters = [
        {
          ...firstLetter,
          id: firstLetter.id || makeCoachId('open_when'),
          label: firstLetter.label || 'Open When',
          prompt: 'Open when you need the heart of this gift',
          message: firstMessage,
        },
      ];
      if (secondMessage && secondMessage !== firstMessage) {
        letters.push({
          ...secondLetter,
          id: secondLetter.id || makeCoachId('open_later'),
          label: secondLetter.label || 'Open Later',
          prompt: 'Open when you want the final reminder',
          message: secondMessage,
        });
      }
      return {
        ...data,
        label: letters[0].label,
        hint: letters[0].prompt,
        content: letters[0].message,
        letters,
      };
    }, pageData);
  }

  if (answers.peak) {
    modules = updateFirstModuleOfType(modules, 'voice_postcard', (data) => ({
      ...data,
      title: data.title && data.title !== 'A Voice Note' ? data.title : 'A Voice Note',
      prompt: `Record this in your own voice: ${answers.peak}`,
      audioUrl: data.audioUrl || null,
      recordedUrl: data.recordedUrl || null,
    }));
  }

  if (answers.anchor || answers.threshold) {
    modules = updateFirstModuleOfType(modules, ['memory_map', 'timeline'], (data) => {
      const existingItems = Array.isArray(data.timelineItems) ? data.timelineItems : [];
      const firstItem = existingItems[0] || {};
      const secondItem = existingItems[1] || {};
      const timelineItems = [
        {
          ...firstItem,
          id: firstItem.id || makeCoachId('timeline'),
          label: firstItem.label || 'The first memory',
          dateLabel: firstItem.dateLabel || '',
          note: answers.anchor || firstItem.note || '',
          iconType: firstItem.iconType || 'photo',
          linkedModuleId: firstItem.linkedModuleId || '',
        },
        {
          ...secondItem,
          id: secondItem.id || makeCoachId('timeline'),
          label: secondItem.label || 'Why it matters',
          dateLabel: secondItem.dateLabel || '',
          note: answers.threshold || secondItem.note || '',
          iconType: secondItem.iconType || 'heart',
          linkedModuleId: secondItem.linkedModuleId || '',
        },
        ...existingItems.slice(2),
      ];
      return {
        ...data,
        title: data.title || 'Moments to Keep',
        timelineItems,
      };
    });
  }

  if (answers.afterglow || answers.peak) {
    modules = updateFirstModuleOfType(modules, 'keepsake_poster', (data) => ({
      ...data,
      title: data.title && data.title !== 'Keep This Moment' ? data.title : 'Keep This Moment',
      senderName: data.senderName || 'With Love',
      message: answers.afterglow || answers.peak,
      closingLine: answers.afterglow && answers.peak ? answers.peak : (data.closingLine || 'Come back whenever you need to remember this.'),
      posterStyle: data.posterStyle || 'macaron',
    }));
  }

  return {
    ...pageData,
    modules,
  };
};
